import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  adminUserGrowthSnapshotAnswersBeginningAction,
  adminUserGrowthSnapshotAnswersEndAction,
  adminUserGrowthSnapshotAnswersLookingBackAction,
  adminUserGrowthSnapshotAnswersResultAction
} from "../Action/action";

const PHASE_META = {
  beginning: { title: "Start Answers", backLabel: "Growth snapshot answers" },
  end: { title: "End Answers", backLabel: "Growth snapshot answers" },
  result: { title: "Growth Snapshot Result", backLabel: "Growth snapshot answers" },
  "looking-back": { title: "Looking Back Question Answers", backLabel: "Growth snapshot answers" }
};

export default function UserGrowthSnapshotAnswers() {
  const { userId, phase } = useParams();
  const navigate = useNavigate();
  const safePhase = ["beginning", "end", "result", "looking-back"].includes(String(phase))
    ? String(phase)
    : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const meta = PHASE_META[safePhase] || { title: "Growth Snapshot Answers", backLabel: "User" };

  useEffect(() => {
    if (!userId || !safePhase) {
      setLoading(false);
      setError("Invalid page.");
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        let res;
        if (safePhase === "beginning") res = await adminUserGrowthSnapshotAnswersBeginningAction(userId);
        else if (safePhase === "end") res = await adminUserGrowthSnapshotAnswersEndAction(userId);
        else if (safePhase === "looking-back") res = await adminUserGrowthSnapshotAnswersLookingBackAction(userId);
        else res = await adminUserGrowthSnapshotAnswersResultAction(userId);

        if (cancelled) return;
        if (!res?.success) {
          setPayload(null);
          setError(res?.message || res?.msg || "Failed to load answers.");
          return;
        }
        setPayload(res.data || null);
      } catch (e) {
        if (cancelled) return;
        setPayload(null);
        setError(e?.response?.data?.message || e?.message || "Failed to load answers.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, safePhase]);

  const purchaseLabel = useMemo(() => {
    const p = payload?.purchase;
    if (!p) return null;
    return [p.course_title, p.id ? `Purchase #${p.id}` : null].filter(Boolean).join(" · ");
  }, [payload]);

  const items = Array.isArray(payload?.items) ? payload.items : [];
  const result = payload?.result || null;
  const categories = Array.isArray(result?.categories) ? result.categories : [];

  return (
    <div className="content">
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/users">Users</Link>
          <span>›</span>
          <Link to={`/users/${userId}`}>User</Link>
          <span>›</span>
          <span>{meta.title}</span>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate(`/users/${userId}`)}>
          ← Back
        </button>
      </div>

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-header">
          <h2>{meta.title}</h2>
        </div>
        {purchaseLabel ? <p className="help-text" style={{ marginBottom: 16 }}>{purchaseLabel}</p> : null}

        {loading ? (
          <div className="empty-state" style={{ padding: "28px 12px" }}>
            Loading…
          </div>
        ) : safePhase === "result" ? (
          result ? (
            <div>
              {result.status === "unavailable" || result.unavailable_message ? (
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, marginBottom: 20 }}>
                  {result.unavailable_message || "Growth snapshot result is unavailable."}
                </div>
              ) : null}

              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Label</th>
                      <th>Reflection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length || result.overall_reflection_text || result.overall_internal_profile ? (
                      <>
                        {categories.map((c) => (
                          <tr key={c.category_key || c.category_title}>
                            <td>
                              <strong>{c.category_title || c.category_key || "—"}</strong>
                            </td>
                            <td>{c.reflection_label || c.reflection_level || "—"}</td>
                            <td style={{ whiteSpace: "pre-wrap" }}>{c.reflection_text || "—"}</td>
                          </tr>
                        ))}
                        {result.overall_reflection_text || result.overall_internal_profile ? (
                          <tr key="overall-reflection">
                            <td>
                              <strong>Overall Reflection</strong>
                            </td>
                            <td>{result.overall_internal_profile || "—"}</td>
                            <td style={{ whiteSpace: "pre-wrap" }}>
                              {result.overall_reflection_text || "—"}
                            </td>
                          </tr>
                        ) : null}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={3}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No category results available.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: "28px 12px" }}>
              No growth snapshot result available.
            </div>
          )
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Question</th>
                  <th>Answer</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? (
                  items.map((item, idx) => (
                    <tr key={`${item.response_id || item.question_key || item.question_id || idx}`}>
                      <td>{idx + 1}</td>
                      <td>
                        <strong>{item.question || "—"}</strong>
                        {item.section_title || item.step_title ? (
                          <div className="help-text" style={{ marginTop: 4 }}>
                            {[item.section_title, item.step_title].filter(Boolean).join(" · ")}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ whiteSpace: "pre-wrap" }}>{item.answer || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        No answers found for this phase.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
